import React, { useEffect, useRef, useState } from 'react';
import { Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  days: number[];
  onChange: (next: number[]) => void;
  maxCount: number;
  maxDays: number;
  /** How many rows must stay (appointment reminders keep one, so switched on they always send). */
  minCount?: number;
  /** Furthest first ('desc') or soonest first ('asc'), applied when a box is left. */
  order?: 'desc' | 'asc';
  /** The words after the number, e.g. "days before the visit". */
  unit: (days: number) => string;
  /** A label in front of each row ("Reminder 1"); none in a compact table cell. */
  rowLabel?: (index: number) => string;
  /** Accessible names of row i's box and its remove button. */
  fieldLabel: (index: number) => string;
  removeLabel: (index: number) => string;
  addLabel: string;
  disabled?: boolean;
  compact?: boolean;
}

/** Days a new row starts at, in order of preference: the first one not already listed. */
const CANDIDATES = [1, 0, 2, 3, 7, 14, 30, 60, 90];

/**
 * Reminder timing as one row per reminder: a number box, the words after it, and a remove button,
 * plus an Add button. Replaced the "+ days" box, which read as "3 days plus more" (Ali, 2026-10-22).
 * Each box keeps its raw text while typed (frontend.md, controlled inputs), so an emptied box can be
 * retyped; leaving it sorts the rows, drops a repeated day and puts back an unusable entry.
 */
const ReminderDaysEditor: React.FC<Props> = ({
  days,
  onChange,
  maxCount,
  maxDays,
  minCount = 0,
  order = 'desc',
  unit,
  rowLabel,
  fieldLabel,
  removeLabel,
  addLabel,
  disabled,
  compact,
}) => {
  const [drafts, setDrafts] = useState<string[]>(() => days.map(String));
  // The list last seen or sent; a different `days` (a load, Discard) re-seeds the boxes.
  const seeded = useRef(days.join(','));
  useEffect(() => {
    const key = days.join(',');
    if (key !== seeded.current) {
      seeded.current = key;
      setDrafts(days.map(String));
    }
  }, [days]);

  const send = (next: number[]) => {
    seeded.current = next.join(',');
    onChange(next);
  };
  const parse = (text: string): number | null => {
    if (!/^\d+$/.test(text.trim())) return null;
    const n = Number(text.trim());
    return n <= maxDays ? n : null;
  };
  const tidy = (list: number[]) =>
    Array.from(new Set(list)).sort((a, b) => (order === 'asc' ? a - b : b - a));

  const change = (i: number, text: string) => {
    setDrafts((prev) => prev.map((d, j) => (j === i ? text : d)));
    const n = parse(text);
    if (n !== null && n !== days[i]) send(days.map((d, j) => (j === i ? n : d)));
  };
  const leave = () => {
    const next = tidy(days);
    setDrafts(next.map(String));
    if (next.join(',') !== days.join(',')) send(next);
  };
  const remove = (i: number) => {
    const next = days.filter((_, j) => j !== i);
    setDrafts(next.map(String));
    send(next);
  };
  const add = () => {
    const free =
      CANDIDATES.find((c) => c <= maxDays && !days.includes(c)) ??
      Array.from({ length: maxDays + 1 }, (_, d) => d).find((d) => !days.includes(d));
    if (free === undefined) return;
    const next = tidy([...days, free]);
    setDrafts(next.map(String));
    send(next);
  };

  const removeButton = (i: number, tight?: boolean) =>
    !disabled && drafts.length > minCount ? (
      <IconButton size="small" aria-label={removeLabel(i)} onClick={() => remove(i)} sx={tight ? { p: 0.25 } : undefined}>
        <CloseIcon fontSize="small" />
      </IconButton>
    ) : null;
  const addButton =
    !disabled && drafts.length < maxCount ? (
      <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ textTransform: 'none' }}>
        {addLabel}
      </Button>
    ) : null;

  // In a table cell the days sit side by side (the column header names the unit) and Add is a small
  // + button, so a type stays on one line.
  if (compact) {
    return (
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap columnGap={0.75} rowGap={0.5}>
        {drafts.map((text, i) => (
          <Stack key={i} direction="row" alignItems="center">
            <TextField
              size="small"
              value={text}
              onChange={(e) => change(i, e.target.value)}
              onBlur={leave}
              error={parse(text) === null}
              disabled={disabled}
              inputProps={{ inputMode: 'numeric', 'aria-label': fieldLabel(i), style: { textAlign: 'center', padding: '6px 2px' } }}
              sx={{ width: 46 }}
            />
            {removeButton(i, true)}
          </Stack>
        ))}
        {!disabled && drafts.length < maxCount && (
          <Tooltip title="Add a day">
            <IconButton size="small" color="primary" aria-label={addLabel} onClick={add} sx={{ p: 0.25 }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={1} alignItems="flex-start">
      {drafts.map((text, i) => {
        const n = parse(text);
        return (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            {rowLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ width: 84, flex: 'none', display: { xs: 'none', sm: 'block' } }}>
                {rowLabel(i)}
              </Typography>
            )}
            <TextField
              size="small"
              value={text}
              onChange={(e) => change(i, e.target.value)}
              onBlur={leave}
              error={n === null}
              disabled={disabled}
              inputProps={{ inputMode: 'numeric', 'aria-label': fieldLabel(i), style: { textAlign: 'center' } }}
              sx={{ width: 72, flex: 'none' }}
            />
            <Typography variant="body2">
              {unit(n ?? days[i] ?? 0)}
            </Typography>
            {removeButton(i)}
          </Stack>
        );
      })}
      {addButton}
    </Stack>
  );
};

export default ReminderDaysEditor;
